import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Chip from 'tio-ui/components/chip';
import PageHeader from 'tio-ui/components/page-header';
import ElfTile from 'tio-employee/components/slr/elf-tile';
import ReimaginedContributionHistory from 'tio-employee/components/slr/contribution-history';
import LoanStandingAttentionCard from 'tio-employee/components/slr/loan-standing-attention-card';
import LoanStandingUploadDrawer from 'tio-employee/components/slr/loan-standing-upload-drawer';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import {
  elfTileState,
  isLrapEnrolled,
  shouldShowElfTile,
} from 'tio-employee/utils/lrap-enrollment';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type FeaturesService from 'tio-common/services/features';
import type SessionContextService from 'tio-employee/services/session-context';
import type { AuthenticatedSlrDashboardModel } from 'tio-employee/routes/authenticated/slr/dashboard';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';

interface SlrDashboardSignature {
  Args: {
    model: AuthenticatedSlrDashboardModel;
  };
}

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service declare features: FeaturesService;
  @service declare sessionContext: SessionContextService;

  // Post-upload refresh result, keyed to the model value it superseded. A
  // route refresh (e.g. switching the receiving account re-runs the model
  // hook) replaces the model value, which invalidates this override
  // automatically — a plain tracked copy seeded from args would keep showing
  // the OLD account's alert after the model refreshed underneath it.
  @tracked private refreshedLoanStanding?: {
    supersedes: SlrLoanStandingAuditState | null;
    state: SlrLoanStandingAuditState | null;
  };
  @tracked isLoanStandingUploadDrawerOpen = false;

  get loanStandingVerification() {
    const fromModel = this.args.model.loanStandingVerification;
    const refreshed = this.refreshedLoanStanding;
    return refreshed && refreshed.supersedes === fromModel ? refreshed.state : fromModel;
  }

  get reimaginedModel() {
    return this.args.model.reimagined;
  }

  get lrapEnrollmentStatus() {
    return this.args.model.lrapEnrollmentStatus;
  }

  get showElfTile() {
    return shouldShowElfTile(this.features.isEnabled('lrapEnrollment'), this.lrapEnrollmentStatus);
  }

  get elfTileState() {
    return elfTileState(this.lrapEnrollmentStatus);
  }

  get isLrapEnrolled() {
    return isLrapEnrolled(this.lrapEnrollmentStatus);
  }

  /**
   * The plan's selected eligibility-condition names — drives which upload
   * guidelines the verification card lists (tio-admin →
   * plans/eligibility-conditions-editor is the source of truth).
   */
  get planEligibilityConditions() {
    return this.sessionContext.currentEmployee?.plan?.eligibilityConditions?.map((c) => c.name);
  }

  get showLoanStandingAlert() {
    // Hard-ineligible verdicts render the page-level banner instead of an
    // attention card (Q1, page states 670-466 / 1031-2202) — the banner lives
    // in contribution-history, which owns the layout slot between hero and
    // Loan Accounts.
    if (this.loanStandingVerification?.status === 'ineligible') return false;
    return shouldShowSlrLoanStandingAlert(this.loanStandingVerification);
  }

  /**
   * The state flipped to eligible during THIS session (post-upload refresh) —
   * show the success confirmation once. A page that loads already-eligible
   * stays quiet (no attention section), matching the design's verified state.
   */
  get justVerified() {
    const refreshed = this.refreshedLoanStanding;
    if (refreshed?.state?.status !== 'eligible') return false;
    // Same identity check as loanStandingVerification: the refresh only
    // counts while it supersedes the CURRENT route model. After an account
    // switch/route refresh replaces the model, a stale eligible refresh must
    // not resurrect the success section over a page that loaded verified.
    if (refreshed.supersedes !== this.args.model.loanStandingVerification) return false;
    const previous = refreshed.supersedes;
    return !!previous && previous.status !== 'eligible';
  }

  get showAttentionSection() {
    return this.showLoanStandingAlert || this.justVerified;
  }

  openLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = true;
  };

  /**
   * Records the Review Name panel answer (fuzzy name match). The service
   * PATCHes name_match_confirmation and returns the settled post-eval state,
   * applied through the same supersedes-tracking as the upload refresh so
   * justVerified fires when a confirmation flips the state to eligible.
   */
  confirmLoanStandingName = async (confirmed: boolean) => {
    const id = this.loanStandingVerification?.employeePlanAccountId;
    if (!id) return;
    const supersedes = this.args.model.loanStandingVerification;
    const state = await this.slrLoanStandingVerification.confirmNameMatch(id, confirmed);
    this.refreshedLoanStanding = { supersedes, state };
  };

  closeLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = false;
  };

  refreshLoanStandingVerification = dropTask(async () => {
    const supersedes = this.args.model.loanStandingVerification;
    const state = await this.slrLoanStandingVerification.fetchCurrentState();
    this.refreshedLoanStanding = { supersedes, state };
    // The scoped refresh can resolve to null (e.g. the receiving account
    // changed mid-flow); close the upload drawer instead of letting the
    // surrounding {{#if}} unmount it while its open flag is still true.
    if (!state) {
      this.isLoanStandingUploadDrawerOpen = false;
    }
  });

  <template>
    <div class="tio-col-tight">
      <PageHeader @title={{t "dashboard_tiles.student_loan_repayment"}} @sticky={{false}}>
        <:actions>
          {{#if this.isLrapEnrolled}}
            <Chip @intent="success" @size="sm" data-test-lrap-enrolled-chip>
              {{t "lrap.enrollment.tile.enrolled"}}
            </Chip>
          {{/if}}
        </:actions>
      </PageHeader>
      {{#if this.showElfTile}}
        <ElfTile @state={{this.elfTileState}} />
      {{/if}}
      <ReimaginedContributionHistory
        @model={{this.reimaginedModel}}
        @loanStandingState={{this.loanStandingVerification}}
        @slotAttentionCount={{if this.showAttentionSection 1 0}}
      >
        <:attention>
          {{! Card only — the unified "Needs Your Attention" section (one
              header covering this card and the server action items) is
              contribution-history's; it places the section below the hero,
              or at the top for page states without one (#9887, #9910). }}
          {{#if this.loanStandingVerification}}
            {{#if this.showAttentionSection}}
              <LoanStandingAttentionCard
                @state={{this.loanStandingVerification}}
                @eligibilityConditions={{this.planEligibilityConditions}}
                @onOpenUpload={{this.openLoanStandingUploadDrawer}}
                @onConfirmName={{this.confirmLoanStandingName}}
                data-test-slr-loan-standing-attention
              />
            {{/if}}
          {{/if}}
        </:attention>
      </ReimaginedContributionHistory>
    </div>

    {{#if this.loanStandingVerification}}
      <LoanStandingUploadDrawer
        @isOpen={{this.isLoanStandingUploadDrawerOpen}}
        @state={{this.loanStandingVerification}}
        @onClose={{this.closeLoanStandingUploadDrawer}}
        @onRefresh={{this.refreshLoanStandingVerification.perform}}
      />
    {{/if}}
  </template>
}
